import axios from 'axios';
import config from '../config/env.config.js';

// Interfaces
export interface VerificarIdentidadData {
  nombre: string;
  email: string;
  documento: string;
  telefono: string;
  tipoDocumento: string;
  cuit?: string;
  documentoPersoneria?: string; // Base64 del documento
  selfieUrl?: string;
  documentoUrl?: string;
}

// Start of Selection
const API_BASE_URL = config.API_URL;
console.log('🔗 API Base URL:', API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token de autenticación
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    
    // Si el error es 401 (no autorizado), limpiar token y redirigir al login
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      window.location.href = '/login';
    }
    
    // Si es un error de red, no hacer nada especial
    if (error.code === 'ERR_NETWORK') {
      console.error('Network error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

export const authApi = {
  // Endpoints de autenticación
  login: async (data: { email: string; password: string }) => {
    return api.post('/auth/login', data);
  },

  register: async (data: {
    email: string;
    password: string;
    nombre: string;
    apellido: string;
    rol: 'ADMIN' | 'USER';
  }) => {
    return api.post('/auth/register', data);
  },

  logout: async () => {
    return api.post('/auth/logout');
  },

  getProfile: async () => {
    return api.get('/auth/profile');
  },

  refreshToken: async () => {
    return api.post('/auth/refresh-token');
  },

  changePassword: async (data: {
    currentPassword: string;
    newPassword: string;
  }) => {
    return api.post('/auth/change-password', data);
  },
};

export const firmaApi = {
  // Endpoints existentes
  generarLinkFirma: async (data: {
    email: string;
    nombre: string;
    titulo: string;
    descripcion?: string;
  }) => {
    return api.post('/firma/generar-link', data);
  },

  obtenerLink: async (token: string) => {
    return api.get(`/firma/link/${token}`);
  },

  validarEmailFirmante: async (token: string, email: string) => {
    return api.post('/firma/validar-email', { token, email });
  },

  marcarDocumentoComoFirmado: async (token: string) => {
    return api.post(`/firma/marcar-firmado/${token}`);
  },

  verificarIdentidad: async (data: {
    nombre: string;
    email: string;
    documento: string;
    telefono: string;
    tipoDocumento: string;
  }) => {
    return api.post('/firma/verificar-identidad', data);
  },

  firmarDocumento: async (data: {
    firmante: string;
    email: string;
    documento: string;
    titulo: string;
    verificacionId: string;
    s3Path?: string;
  }) => {
    return api.post('/firma/firmar-documento', data);
  },

  obtenerEstado: async (id: string) => {
    return api.get(`/firma/estado/${id}`);
  },

  cancelarAcuerdo: async (token: string, motivo?: string) => {
    return api.post(`/firma/cancelar-acuerdo/${token}`, { motivo });
  },

  // Nuevos endpoints para el panel de administración
  subirDocumento: async (data: {
    file: string; // Base64
    fileName: string;
    titulo: string;
    descripcion?: string;
    categoria: string;
    fechaExpiracion?: string;
    firmante: {
      email: string;
      nombre: string;
      telefono: string;
      tipoDocumento: string;
      numeroDocumento: string;
      esEmpresa: boolean;
      cuit?: string;
      documentoPersoneria?: string; // Base64 del documento
    };
    userId: number;
  }) => {
    return api.post('/admin/upload-document', data);
  },

  agregarFirmante: async (documentId: string, data: {
    firmante: {
      email: string;
      nombre: string;
      telefono: string;
      tipoDocumento: string;
      numeroDocumento: string;
      esEmpresa: boolean;
      cuit?: string;
      documentoPersoneria?: string; // Base64 del documento
    };
  }) => {
    return api.post(`/admin/document/${documentId}/add-signer`, data);
  },

  listarDocumentos: async () => {
    return api.get('/admin/documents');
  },

  obtenerDocumento: async (id: string) => {
    return api.get(`/admin/document/${id}`);
  },

  reenviarEmail: async (linkId: string) => {
    return api.post('/admin/send-email', { linkId });
  },

  aprobarVerificacion: async (data: {
    verificationId: string;
    action: 'APPROVE' | 'REJECT';
    comments?: string;
  }) => {
    return api.post('/admin/approve-verification', data);
  },

  descargarDocumento: async (agreementId: string) => {
    return api.get(`/admin/download/${agreementId}`, {
      responseType: 'blob'
    });
  },

  // Nuevos endpoints para el cliente
  enviarOTP: async (data: { telefono: string }) => {
    return api.post('/firma/send-otp', data);
  },

  verificarOTP: async (data: { telefono: string; codigo: string }) => {
    return api.post('/firma/verify-otp', data);
  },

  subirDocumentosVerificacion: async (formData: FormData) => {
    return api.post('/firma/upload-verification-documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  obtenerEstadoVerificacion: async (verificationId: string) => {
    return api.get(`/firma/verification-status/${verificationId}`);
  },

  firmarDocumentoCliente: async (data: { linkId: string; confirmacion: boolean }) => {
    return api.post('/firma/firmar-documento-cliente', data);
  },

  // Endpoints de Adobe Sign
  obtenerAuthUrl: async () => {
    return api.get('/adobe-sign/auth-url');
  },

  testService: async () => {
    return api.get('/adobe-sign/test-service');
  },

  intercambiarToken: async (data: { code: string }) => {
    return api.post('/adobe-sign/exchange-token', data);
  },

  refrescarToken: async (data: { refresh_token: string }) => {
    return api.post('/adobe-sign/refresh-token', data);
  },

  crearAcuerdo: async (data: {
    documentData: string; // Base64
    fileName: string;
    signerEmail: string;
    signerName: string;
    agreementName?: string;
  }) => {
    return api.post('/adobe-sign/create-agreement', data);
  },

  obtenerUrlFirma: async (agreementId: string, email: string) => {
    return api.get(`/adobe-sign/agreement/${agreementId}/signing-url?email=${email}`);
  },

  obtenerEstadoAcuerdo: async (agreementId: string) => {
    return api.get(`/adobe-sign/agreement/${agreementId}/status`);
  },

  descargarDocumentoFirmado: async (agreementId: string) => {
    return api.get(`/adobe-sign/agreement/${agreementId}/download`, {
      responseType: 'blob'
    });
  },

  cancelarAcuerdo: async (token: string, motivo?: string) => {
    return api.post(`/firma/cancelar-acuerdo/${token}`, { motivo });
  },

  listarAcuerdos: async () => {
    return api.get('/adobe-sign/agreements');
  },
};

export const adminApi = {
  // Endpoints de administración
  subirDocumento: async (data: FormData) => {
    return api.post('/admin/upload-document', data);
  },

  subirDocumentoMultiFirmantes: async (data: {
    archivo: string;
    titulo: string;
    descripcion?: string;
    categoria: string;
    fechaExpiracion?: string;
    firmantes: string; // JSON string
  }) => {
    return api.post('/admin/subir-documento', data);
  },

  listarDocumentos: async () => {
    return api.get('/admin/documents');
  },

  obtenerDocumento: async (id: string) => {
    return api.get(`/admin/document/${id}`);
  },

  reenviarEmail: async (linkId: string) => {
    return api.post('/admin/send-email', { linkId });
  },

  aprobarVerificacion: async (data: {
    verificationId: string;
    action: 'APPROVE' | 'REJECT';
    comments?: string;
  }) => {
    return api.post('/admin/approve-verification', data);
  },

  descargarDocumento: async (agreementId: string) => {
    return api.get(`/admin/download/${agreementId}`, { responseType: 'blob' });
  },

  obtenerPendientesAprobacion: async () => {
    return api.get('/admin/pending-approvals');
  },

  obtenerDocumentosVerificacion: async (id: string) => {
    console.log('🔗 Llamando endpoint:', `/admin/verification/${id}/documents`);
    return api.get(`/admin/verification/${id}/documents`);
  },

  // Nuevos endpoints para clientes
  listarClientes: async () => {
    return api.get('/admin/clients');
  },

  obtenerCliente: async (id: string) => {
    return api.get(`/admin/client/${id}`);
  },

  obtenerDocumentosPorCliente: async (id: string) => {
    return api.get(`/admin/client/${id}/documents`);
  },

  // Método para agregar firmante a un documento
  agregarFirmante: async (documentId: string, data: {
    firmante: {
      email: string;
      nombre: string;
      telefono: string;
      tipoDocumento: string;
      numeroDocumento: string;
      esEmpresa: boolean;
    };
  }) => {
    return api.post(`/admin/document/${documentId}/add-signer`, data);
  },

  // Método para buscar firmante existente
  buscarFirmante: async (email: string, numeroDocumento: string) => {
    return api.get(`/admin/clients?email=${email}&numeroDocumento=${numeroDocumento}`);
  },

  // Métodos para documentos firmados
  getSignedDocumentsByUser: async (userId: number) => {
    return api.get(`/admin/signed-documents/${userId}`);
  },
  
  getDocumentStatus: async (agreementId: string) => {
    return api.get(`/admin/document-status/${agreementId}`);
  },
  
  downloadSignedDocument: async (agreementId: string) => {
    return api.get(`/admin/download/${agreementId}`, { responseType: 'blob' });
  },

  // Obtener detalles completos de un documento
  getDocumentoDetallesCompletos: async (documentoId: number) => {
    return api.get(`/admin/documento/${documentoId}/detalles-completos`);
  },

  // Cancelar acuerdo desde admin
  cancelarAcuerdo: async (token: string, motivo?: string) => {
    return api.post(`/firma/cancelar-acuerdo/${token}`, { motivo });
  },

  // Obtener auditoría de webhooks por documento
  getWebhookAudit: async (documentoId: number) => {
    return api.get(`/admin/documento/${documentoId}/webhook-audit`);
  },

  // Obtener lista de documentos
  getDocumentos: async () => {
    return api.get('/admin/documentos');
  },

  // Obtener documento por ID
  getDocumento: async (documentoId: number) => {
    return api.get(`/admin/documento/${documentoId}`);
  },

  // Descargar documento original
  downloadDocumentoOriginal: async (documentoId: number) => {
    return api.get(`/admin/documento/${documentoId}/download`, {
      responseType: 'blob'
    });
  },

  // Descargar documento firmado
  downloadDocumentoFirmado: async (agreementId: string) => {
    return api.get(`/admin/documento/agreement/${agreementId}/download-signed`, {
      responseType: 'blob'
    });
  }
};

// API de Adobe Sign
export const adobeSignApi = {
  // Probar conexión con Adobe Sign
  testConnection: async () => {
    return api.get('/adobe-sign/test-connection');
  },

  // Refrescar token manualmente
  refreshToken: async () => {
    return api.post('/adobe-sign/refresh-token');
  },

  // Iniciar proceso de autorización
  startAuth: async () => {
    return api.get('/adobe-sign/auth/start');
  },

  // Obtener estado de autorización
  getAuthStatus: async () => {
    return api.get('/adobe-sign/auth/status');
  },
};


export default api; 
